import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Ren klienttilstand for UI'et.
 *
 * Bevidst afgrænset: serverdata hører hjemme i TanStack Query, ikke her.
 * Dobbeltopbevaring ville betyde to kilder til sandhed, der kan komme ud af
 * trit. Zustand bruges kun til præferencer og midlertidig UI-tilstand.
 */
export type Theme = 'light' | 'dark'

/** Lyst er standard. Mørkt er et valg, brugeren selv træffer. */
export const DEFAULT_THEME: Theme = 'light'

interface UiState {
  /** Lyst eller mørkt tema. Huskes mellem sessioner. */
  theme: Theme
  toggleTheme: () => void
  setTheme: (theme: Theme) => void

  /** Sammenklappet sidebar på desktop. Huskes mellem sessioner. */
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void

  /**
   * Gruppen "På tværs af projekter" i sidebaren. Foldet sammen som
   * udgangspunkt, så de ni endnu ikke byggede moduler ikke fylder menuen.
   * Huskes mellem sessioner — den, der folder den ud, vil have den udfoldet.
   */
  crossProjectNavOpen: boolean
  toggleCrossProjectNav: () => void

  /** Sidebar som drawer på mobil. Huskes bevidst ikke. */
  mobileNavOpen: boolean
  setMobileNavOpen: (open: boolean) => void

  /** Kommandopaletten (Cmd/Ctrl + K). */
  commandPaletteOpen: boolean
  setCommandPaletteOpen: (open: boolean) => void

  /**
   * Sidst besøgte projekt. Bruges til at sende brugeren det rigtige sted hen,
   * når der klikkes på "Overblik". Kun et hint — adgangen valideres altid på
   * serveren.
   */
  lastProjectId: string | null
  setLastProjectId: (projectId: string | null) => void
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: DEFAULT_THEME,
      toggleTheme: () => set((state) => ({ theme: state.theme === 'dark' ? 'light' : 'dark' })),
      setTheme: (theme) => set({ theme }),

      sidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

      crossProjectNavOpen: false,
      toggleCrossProjectNav: () =>
        set((state) => ({ crossProjectNavOpen: !state.crossProjectNavOpen })),

      mobileNavOpen: false,
      setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),

      commandPaletteOpen: false,
      setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),

      lastProjectId: null,
      setLastProjectId: (lastProjectId) => set({ lastProjectId }),
    }),
    {
      name: 'bygoverblik-ui',
      // Kun præferencer gemmes. Åbne dialoger må ikke genopstå ved genindlæsning.
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        crossProjectNavOpen: state.crossProjectNavOpen,
        lastProjectId: state.lastProjectId,
      }),
    },
  ),
)
